/**
 * Calendar Widget
 * Renders a monthly calendar grid with navigation
 *
 * @package Advanced Regeneration
 */

class CalendarWidget {
	constructor(container) {
		this.container = container;
		this.currentDate = new Date();
		this.init();
	}

	init() {
		this.render();
		this.attachEventListeners();
	}

	attachEventListeners() {
		const prevBtn = this.container.querySelector('.calendar-widget__nav-btn--prev');
		const nextBtn = this.container.querySelector('.calendar-widget__nav-btn--next');
		const todayBtn = this.container.querySelector('.calendar-widget__today-btn');

		if (prevBtn) {
			prevBtn.addEventListener('click', () => this.previousMonth());
		}
		if (nextBtn) {
			nextBtn.addEventListener('click', () => this.nextMonth());
		}
		if (todayBtn) {
			todayBtn.addEventListener('click', () => this.goToToday());
		}
	}

	render() {
		const year = this.currentDate.getFullYear();
		const month = this.currentDate.getMonth();

		// Update month display
		const monthDisplay = this.container.querySelector('.calendar-widget__current-month');
		if (monthDisplay) {
			const monthNames = [
				'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
				'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
			];
			monthDisplay.textContent = `${monthNames[month]} ${year}`;
		}

		// Render calendar days
		this.renderDays(year, month);
	}

	renderDays(year, month) {
		const daysContainer = this.container.querySelector('[data-calendar-days]');
		if (!daysContainer) return;

		daysContainer.innerHTML = '';

		// Get first day of month (0 = Sunday, 1 = Monday, etc.)
		const firstDay = new Date(year, month, 1).getDay();
		// Adjust for Monday start (0 = Monday, 6 = Sunday)
		const firstDayAdjusted = firstDay === 0 ? 6 : firstDay - 1;

		// Get number of days in month
		const daysInMonth = new Date(year, month + 1, 0).getDate();

		// Get today's date for highlighting
		const today = new Date();
		const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
		const todayDate = today.getDate();

		// Add empty cells for days before month starts
		for (let i = 0; i < firstDayAdjusted; i++) {
			const emptyDay = document.createElement('div');
			emptyDay.className = 'calendar-widget__day calendar-widget__day--empty';
			daysContainer.appendChild(emptyDay);
		}

		// Add days of the month
		for (let day = 1; day <= daysInMonth; day++) {
			const dayElement = document.createElement('div');
			dayElement.className = 'calendar-widget__day';
			dayElement.textContent = day;

			// Highlight today
			if (isCurrentMonth && day === todayDate) {
				dayElement.classList.add('calendar-widget__day--today');
			}

			daysContainer.appendChild(dayElement);
		}
	}

	previousMonth() {
		this.currentDate.setMonth(this.currentDate.getMonth() - 1);
		this.render();
	}

	nextMonth() {
		this.currentDate.setMonth(this.currentDate.getMonth() + 1);
		this.render();
	}

	goToToday() {
		this.currentDate = new Date();
		this.render();
	}
}

// Initialize all calendar widgets on page
function initCalendarWidgets() {
	const widgets = document.querySelectorAll('.calendar-widget');
	widgets.forEach(widget => new CalendarWidget(widget));
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCalendarWidgets);
} else {
	initCalendarWidgets();
}

// Re-initialize for WordPress block editor
document.addEventListener('block-editor-render', initCalendarWidgets);

export { CalendarWidget, initCalendarWidgets };
